import * as PopoverPrimitive from '@radix-ui/react-popover';
import { AlertTriangle, ExternalLink, FileText, HelpCircle, Link as LinkIcon } from 'lucide-react';
import { Confidences, SourceMethods, formatDateTime, type Confidence } from '@nesc/shared';
import { cn } from '@/lib/utils';

export type EvidenceSource = {
  id?: string;
  field?: string;
  sourceUrl: string | null;
  method: string;
  excerpt: string | null;
  confidence: string;
  fetchedAt: string;
};

/**
 * 抽出確度の表示（docs/09-design-system.md 5.3 ConfidenceMark）。
 * 確度が「低」の項目は画面上で注記する（F04.2）。
 */
export const ConfidenceMark = ({
  level,
  className,
}: {
  level: Confidence | string | null | undefined;
  className?: string;
}) => {
  if (!level || level === 'high') return null;
  const meta = Confidences.meta(level);
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-control border border-dashed border-border-strong px-1.5 text-note text-foreground-muted',
        level === 'low' && 'border-solid border-foreground font-semibold text-foreground',
        className,
      )}
      title={`確度：${meta.label}（${meta.description}）`}
    >
      {level === 'low' ? (
        <AlertTriangle aria-hidden className="size-3.5" />
      ) : (
        <HelpCircle aria-hidden className="size-3.5" />
      )}
      {level === 'low' ? '推定' : `確度${meta.label}`}
    </span>
  );
};

/**
 * 抽出値の根拠（docs/09-design-system.md 5.3 EvidencePopover）。
 * 取得元 URL・取得日時・抽出方法・抜粋・確度を示す。
 */
export const EvidencePopover = ({
  sources,
  label = '出典',
  className,
}: {
  sources: EvidenceSource[];
  label?: string;
  className?: string;
}) => {
  if (sources.length === 0) return null;
  const latest = sources[0];

  return (
    <PopoverPrimitive.Root>
      <PopoverPrimitive.Trigger
        className={cn(
          'inline-flex items-center gap-1 rounded-control px-1.5 py-0.5 text-note text-foreground-muted',
          'transition-colors duration-hover hover:bg-muted hover:text-foreground',
          className,
        )}
        aria-label={`${label}を表示（${sources.length} 件）`}
      >
        <LinkIcon aria-hidden className="size-3.5" />
        <span>
          {label} {formatDateTime(latest.fetchedAt).slice(5, 10)}
        </span>
      </PopoverPrimitive.Trigger>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          sideOffset={6}
          collisionPadding={12}
          className={cn(
            'z-50 w-[min(28rem,calc(100vw-2rem))] rounded-popover border border-border',
            'bg-surface-raised p-3 shadow-popover',
            'data-[state=open]:animate-fade-in',
          )}
        >
          <p className="mb-2 text-note font-semibold text-foreground">
            {label}（{sources.length} 件）
          </p>
          <ul className="max-h-72 space-y-3 overflow-y-auto">
            {sources.map((source, index) => (
              <li key={source.id ?? index} className="space-y-1 border-b border-border pb-3 last:border-0 last:pb-0">
                <div className="flex flex-wrap items-center gap-1.5 text-note text-foreground-muted">
                  <FileText aria-hidden className="size-3.5" />
                  <span>{SourceMethods.label(source.method)}</span>
                  <span aria-hidden>・</span>
                  <span>確度 {Confidences.label(source.confidence)}</span>
                  <span aria-hidden>・</span>
                  <span className="tabular">{formatDateTime(source.fetchedAt)}</span>
                </div>
                {source.sourceUrl && (
                  <a
                    href={source.sourceUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="inline-flex items-center gap-1 break-all text-note text-foreground underline underline-offset-2"
                  >
                    {source.sourceUrl}
                    <ExternalLink aria-hidden className="size-3 shrink-0" />
                  </a>
                )}
                {source.excerpt && (
                  <p className="rounded-control bg-muted p-2 text-note text-foreground-muted">
                    {source.excerpt}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
};
